import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import * as Sentry from "@sentry/react";

// ─────────────────────────────────────────────────────────────
// Chunk Load Error Detection & Recovery Utilities
// ─────────────────────────────────────────────────────────────

const REFRESH_KEY = 'studyforge-chunk-refresh';
const REFRESH_WINDOW_MS = 10000; // 10 seconds

const hasRecentlyRefreshed = (): boolean => {
  const lastRefresh = sessionStorage.getItem(REFRESH_KEY);
  if (!lastRefresh) return false;
  return Date.now() - parseInt(lastRefresh, 10) < REFRESH_WINDOW_MS;
};

const markRefresh = (): void => {
  sessionStorage.setItem(REFRESH_KEY, Date.now().toString());
};

export const isChunkLoadError = (error: Error): boolean => {
  const message = error.message?.toLowerCase() || '';
  const stack = error.stack?.toLowerCase() || '';

  const patterns = [
    'failed to fetch dynamically imported module',
    'importing a module script failed',
    'loading chunk',
    'chunkloaderror',
    'error loading dynamically imported module',
    'failed to load module script',
    "unexpected token '<'",  // CDN/server serving HTML instead of JS
  ];

  return patterns.some(pattern =>
    message.includes(pattern) || stack.includes(pattern)
  );
};

// ─────────────────────────────────────────────────────────────
// Error Boundary Component
// ─────────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
  pendingAutoRefresh: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
      pendingAutoRefresh: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const chunkError = isChunkLoadError(error);
    const shouldAutoRefresh = chunkError && !hasRecentlyRefreshed();

    return {
      hasError: true,
      error,
      isChunkError: chunkError,
      pendingAutoRefresh: shouldAutoRefresh,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React Error Boundary caught an error:", error, errorInfo);

    const chunkError = isChunkLoadError(error);

    // Report to Sentry in production (skip chunk errors — expected during deploys)
    if (import.meta.env.PROD && !chunkError) {
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
      });
    }

    // Auto-refresh for chunk errors if not recently attempted
    if (chunkError && !hasRecentlyRefreshed()) {
      markRefresh();
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  }

  render() {
    if (this.state.hasError) {
      // Chunk error with pending auto-refresh
      if (this.state.pendingAutoRefresh) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4 bg-background">
            <Card className="max-w-md p-6 text-center">
              <RefreshCw className="w-12 h-12 mx-auto text-primary mb-4 animate-spin" />
              <h2 className="text-xl font-bold mb-2">Updating...</h2>
              <p className="text-muted-foreground">
                New version detected. Reloading.
              </p>
            </Card>
          </div>
        );
      }

      // Chunk error but auto-refresh already attempted
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4 bg-background">
            <Card className="max-w-md p-6 text-center">
              <RefreshCw className="w-12 h-12 mx-auto text-primary mb-4" />
              <h2 className="text-xl font-bold mb-2">Update Available</h2>
              <p className="text-muted-foreground mb-4">
                Please refresh to continue.
              </p>
              <Button onClick={() => window.location.reload()} className="w-full">
                Refresh Now
              </Button>
            </Card>
          </div>
        );
      }

      // Generic error (existing behavior)
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-md p-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">
              The app encountered an unexpected error. Please refresh the page to continue.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-xs text-left bg-muted p-2 rounded mb-4 overflow-auto max-h-40">
                {this.state.error.toString()}
              </pre>
            )}
            <Button onClick={() => window.location.reload()} className="w-full">
              Refresh Page
            </Button>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
